import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useWatch } from 'react-hook-form';

import { getBookingEndpoint } from 'reservations/base/constants/endpoints';
import { HTTP_STATUS_CODE_402_PAYMENT_REQUIRED } from 'reservations/base/constants/httpStatusCodes';
import { NEXTABLE_ERROR_CODE } from 'reservations/base/constants/nextable';
import { useErrorSnackbar } from 'reservations/base/hooks/useSnackbar';
import { useModal } from 'reservations/base/providers/ModalProvider';
import { useBookingId } from 'reservations/booking/providers/BookingProvider';
import {
  type BookingMutationResponse,
  BookingMutationResponseSchema
} from 'reservations/booking/types';
import type { BookingFormSchema } from 'reservations/form/components/Form';
import { cleanUpdateBookingFormDataV2 } from 'reservations/form/helpers/cleanBookingFormData';
import { restClient } from 'utility/restClient';
import type { RequestError } from 'utility/restClient';

type UseUpdateBookingOptions = {
  handleSuccess?: () => void | Promise<void>;
};

export const useUpdateBookingMutationV2 = (
  options: UseUpdateBookingOptions = {}
) => {
  const { handleSuccess } = options;
  const { close, setModalContext } = useModal();
  const bookingId = useBookingId();
  const queryClient = useQueryClient();
  const enqueueErrorSnackbar = useErrorSnackbar();
  const availability = useWatch({
    name: 'availability'
  });

  return useMutation<
    BookingMutationResponse,
    RequestError,
    Record<string, any>
  >({
    mutationKey: ['UPDATE_BOOKING'],
    mutationFn: async formData => {
      const endpoint = getBookingEndpoint(bookingId);
      const cleanedFormData = cleanUpdateBookingFormDataV2(
        formData as BookingFormSchema
      );

      const response = await restClient.put(endpoint, {
        data: Object.fromEntries(cleanedFormData)
      });

      try {
        return BookingMutationResponseSchema.parse(response);
      } catch (err) {
        console.error('useUpdateBookingMutationV2: ', err);
        throw err;
      }
    },
    onSuccess: () => {
      close();
      handleSuccess ? handleSuccess() : null;
    },
    onError: error => {
      if (error.code === HTTP_STATUS_CODE_402_PAYMENT_REQUIRED) {
        setModalContext({ cardDeclinedError: true });
        return;
      }

      if (error.code === NEXTABLE_ERROR_CODE.NO_AVAILABILITY) {
        enqueueErrorSnackbar(
          'CREATE_BOOKING_ERROR_SNACKBAR',
          `${availability} is no longer available. Please select a new time.`
        );
        queryClient.invalidateQueries({
          queryKey: ['bentoboxReservations', 'availabilities']
        });
      }
    }
  });
};
